/**
 * The browser/device half of the Discord handshake.
 *
 * Web and native differ only in where the authorization page opens and how the
 * result comes back:
 *
 *   web     - full-page navigation; the callback returns to this origin with a
 *             ticket in the query string, which we read on the next boot.
 *   native  - the system browser (never an in-app webview, which is both an
 *             OAuth anti-pattern and rejected by Discord); the callback hits a
 *             custom-scheme deep link that reopens the app with the ticket.
 */

import { appBaseUrl } from './api.js';

const DEEP_LINK = 'portfoliotracker://auth';

export const isNativeShell = () => Boolean(window.Capacitor?.isNativePlatform?.());

/** Where the server should send the browser once Discord is done with it. */
export function returnUrl() {
  if (isNativeShell()) return DEEP_LINK;
  /*
   * The app's own base URL, not `location.origin`: mounted under /portfolio/ by
   * a reverse proxy, the origin alone would send the user back to a page that
   * does not exist — and would not match the server's allowlist either.
   */
  return appBaseUrl();
}

/**
 * Reads and then erases the ticket the callback left in the address bar, so it
 * cannot be re-shared, bookmarked, or replayed from browser history.
 */
export function takeCallbackParams() {
  const params = new URLSearchParams(window.location.search);
  const ticket = params.get('ticket');
  const error = params.get('error');
  // The confirmation link lands here too, via the API's redirect.
  const verified = params.get('verified');
  const verifyError = params.get('verifyError');
  // So does the password reset link, which carries its token the same way.
  const reset = params.get('reset');
  if (!ticket && !error && !verified && !verifyError && !reset) return null;

  params.delete('ticket');
  params.delete('error');
  params.delete('verified');
  params.delete('verifyError');
  params.delete('reset');
  const query = params.toString();
  window.history.replaceState(
    {},
    '',
    window.location.pathname + (query ? '?' + query : '') + window.location.hash
  );

  return { ticket, error, verified: Boolean(verified), verifyError, reset };
}

/**
 * Sends the user to Discord. On native this resolves as soon as the browser is
 * open; the ticket arrives later through the deep-link listener.
 */
export async function openAuthorizeUrl(url) {
  if (isNativeShell() && window.Capacitor?.Plugins?.Browser) {
    await window.Capacitor.Plugins.Browser.open({ url, presentationStyle: 'popover' });
    return;
  }
  window.location.assign(url);
}

/**
 * Registers the deep-link handler used by the native shells. Returns a function
 * that removes the listener again, and a no-op on the web.
 */
export function onDeepLink(handler) {
  const app = window.Capacitor?.Plugins?.App;
  if (!isNativeShell() || !app) return () => {};

  const listener = app.addListener('appUrlOpen', event => {
    const url = String(event?.url || '');
    if (!url.startsWith(DEEP_LINK)) return;

    const query = url.includes('?') ? url.slice(url.indexOf('?') + 1) : '';
    const params = new URLSearchParams(query);
    window.Capacitor?.Plugins?.Browser?.close?.();
    handler({ ticket: params.get('ticket'), error: params.get('error') });
  });

  return () => { listener?.remove?.(); };
}
